import { useContext, useEffect, useRef, useState } from "react";
import { Container, Nav, Button, Badge } from "react-bootstrap";
import { registerAllModules } from 'handsontable/registry';
import "handsontable/dist/handsontable.full.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { BiSolidFilePlus } from "react-icons/bi";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { personalSheetsCrud, sharedSheetsCrud } from "../api/sheetApi";
import ReactNotifications from "./common/reactNotifications/ReactNotifications";
import { RiDeleteBin6Line, RiArrowRightSLine } from "react-icons/ri";
import DeleteModal from "./common/commonModal/DeleteModal";
import { categoryCrud } from "../api/category";
import { companyCrud } from "../api/company";
import { yearCrud } from "../api/yearApi";
import { downloadToCsv } from "../api/financeApi";
import PaginationComponent from "./common/pagination/Pagination";
import { TbShareOff, TbShare } from "react-icons/tb";
import { FaFileExport, FaPlus } from "react-icons/fa";
import { unitCrud } from "../api/UnitsApi";
import Context from "../context/Context";
import Sheet from "./business&enterpriseMode/drive/Sheet";

registerAllModules();

const DashboardTable = () => {
  const tableRef = useRef();
  const { tab } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = localStorage.getItem('userId');
  const { setCurrentSheetIndex } = useContext(Context);

  const [sharedSheets, setSharedSheets] = useState([]);
  const [personalSheets, setPersonalSheets] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [sheetToDelete, setSheetToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingSheetId, setLoadingSheetId] = useState(null);
  const [selectedSheets, setSelectedSheets] = useState([]);
  const lastRequestRef = useRef("");

  const itemsPerPage = 10;
  const BRAND_COLOR = "#AEF468";
  const [searchQuery, setSearchQuery] = useState(null);


  useEffect(() => {
    if (location.state?.searchQuery) {
      setSearchQuery(location.state.searchQuery);
    }
  }, [location.state]);

  useEffect(() => {
    categoryCrud({ page: false }, 'get', () => { });
    companyCrud({ page: false }, 'get', () => { });
    yearCrud({ page: false }, 'get', () => { });
    unitCrud({ page: false }, 'get', () => { });
  }, []);

  useEffect(() => {
    setSelectedSheets([]);
  }, [tab]);

  const fetchSheets = (payload, crudFunction, setterFunction) => {
    setLoading(true);
    crudFunction(payload, 'get', (response) => {
      if (response?.status === true) {
        setTotalCount(response?.count);
        setterFunction(response?.data);
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    const activeTab = tab;
    const crudFunction =
      activeTab === "shared" ? sharedSheetsCrud : personalSheetsCrud;
    const setterFunction =
      activeTab === "shared" ? setSharedSheets : setPersonalSheets;

    const page = Number(new URLSearchParams(location.search).get("p") || 1);

    const requestKey = JSON.stringify({ tab, page, searchQuery });

    if (lastRequestRef.current === requestKey) return;
    lastRequestRef.current = requestKey;

    setLoading(true);

    if (searchQuery) {
      fetchSheets({ search: searchQuery }, crudFunction, setterFunction);
      setCurrentPage(1);

      setTimeout(() => {
        navigate(location.pathname, { replace: true, state: null });
      }, 0);
    } else {
      fetchSheets({ page }, crudFunction, setterFunction);
      setCurrentPage(page);
    }

  }, [tab, location.search, searchQuery]);



  const handlePageChange = (page) => {
    const params = new URLSearchParams(location.search);

    if (page > 1) params.set("p", page);
    else params.delete("p");

    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };


  const confirmDelete = () => {
    setLoading(true);
    personalSheetsCrud({ id: sheetToDelete }, "delete", (response) => {
      if (response?.status === true) {
        const page = new URLSearchParams(location.search).get('p') || 1;
        fetchSheets({ page: parseInt(page) }, personalSheetsCrud, setPersonalSheets);
        ReactNotifications("Sheet deleted successfully", "success");
      }
      setShowModal(false);
      setLoading(false);
      setSheetToDelete(null);
    });
  };

  const handleDownload = (ids) => {
    if (ids.length > 0) downloadToCsv({ sheet_ids: ids }, () => { });
  };

  const handleSelect = (id) => {
    setSelectedSheets((prev) =>
      prev.includes(id) ? prev.filter((sId) => sId !== id) : [...prev, id]
    );
  };

  const sheets = tab === "shared" ? sharedSheets : personalSheets;

  return (
    <Container fluid className="px-md-2 py-3 min-vh-100" style={{ backgroundColor: '#fcfcfc' }}>
      <div className="d-flex flex-column flex-md-row justify-content-end align-items-md-center mb-4 gap-3">
        <div className="d-flex gap-2">
          {selectedSheets.length > 0 && (
            <Button
              variant="light"
              className="d-flex align-items-center gap-2 border shadow-sm px-4 py-2 bg-white"
              onClick={() => handleDownload(selectedSheets)}
              style={{ fontWeight: '600', borderRadius: '10px' }}
            >
              <FaFileExport size={14} /> Export ({selectedSheets.length})
            </Button>
          )}
          <Button
            className="d-flex align-items-center gap-2 px-4 py-2 border-0 shadow-sm text-black"
            onClick={() => tableRef.current.handleCreateSheet(tab)}
            style={{ backgroundColor: BRAND_COLOR, fontWeight: '700', borderRadius: '10px' }}
          >
            <FaPlus size={14} /> Create {tab === "shared" ? "Shared" : "Personal"}
          </Button>
        </div>
      </div>

      <div className="bg-white border shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between p-3 gap-3 border-bottom">
          <Nav variant="pills" className="bg-light p-1 rounded-3" style={{ width: 'fit-content' }}>
            <Nav.Item>
              <Nav.Link
                as={Link}
                to="/dashboard/shared"
                eventKey="shared"
                active={tab === 'shared'}
                className="d-flex align-items-center px-4 py-2 fw-bold small transition-all"
                style={{ borderRadius: '8px', whiteSpace: 'nowrap' }}
              >
                <TbShare size={18} className="me-2" /> Shared
              </Nav.Link>
            </Nav.Item>

            <Nav.Item>
              <Nav.Link
                as={Link}
                to="/dashboard/personal"
                eventKey="personal"
                active={tab === 'personal'}
                className="d-flex align-items-center px-4 py-2 fw-bold small transition-all"
                style={{ borderRadius: '8px', whiteSpace: 'nowrap' }}
              >
                <TbShareOff size={18} className="me-2" /> Personal
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </div>

        {sheets.length > 0 && (
          <div className="px-4 py-2 bg-white border-bottom d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <input
                type="checkbox"
                className="form-check-input mt-0 shadow-none border-secondary custom-checkbox"
                checked={selectedSheets.length === sheets.length}
                onChange={(e) => setSelectedSheets(e.target.checked ? sheets.map(s => s.id) : [])}
                style={{ cursor: 'pointer', width: '20px', height: '20px' }}
              />
              <span className="ms-3 fw-bold text-muted text-uppercase" style={{ fontSize: '11px', letterSpacing: '1px' }}>
                Sheet Title
              </span>
            </div>
            <span className="d-none d-md-block fw-bold text-muted text-uppercase" style={{ fontSize: '11px', letterSpacing: '1px' }}>
            </span>
          </div>
        )}

        <div className="p-0 min-vh-50">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <div className="custom-loader"></div>
            </div>
          ) : sheets.length === 0 ? (
            <div className="text-center py-5 my-5">
              <div className="rounded-circle d-inline-flex p-4 mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                <BiSolidFilePlus size={48} className="text-dark" />
              </div>
              <h4 className="fw-bold">No documents yet</h4>
              <p className="text-muted mx-auto mb-4" style={{ maxWidth: '350px' }}>
                Start your workspace by creating a new document.
              </p>
            </div>
          ) : (
            sheets.map((sheet, index) => (
              <div
                key={sheet.id}
                className="px-4 py-3 d-flex align-items-center justify-content-between sheet-row-item"
                onClick={() => {
                  setLoadingSheetId(sheet.id);
                  tableRef.current.handleClick(sheet, sheet.id, tab);
                  setCurrentSheetIndex(sheet.id);
                  setTimeout(() => setLoadingSheetId(null), 100);
                }}
              >
                <div className="d-flex align-items-center gap-3 overflow-hidden">
                  <input
                    type="checkbox"
                    className="form-check-input m-0 shadow-none !cursor-pointer custom-checkbox"
                    checked={selectedSheets.includes(sheet.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => { e.stopPropagation(); handleSelect(sheet.id); }}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <div className="p-2 rounded-3 d-none d-sm-flex" style={{ backgroundColor: '#f8f9fa' }}>
                    <BiSolidFilePlus size={26} className="text-dark" />
                  </div>

                  <div className="d-flex align-items-center gap-2 text-truncate">
                    <h6 className="mb-0 font-medium text-dark text-truncate" style={{ fontSize: '16px' }}>
                      {sheet.title || `Sheet_${index + 1}`}
                    </h6>

                    {loadingSheetId === sheet.id && (
                      <div className="small-loader"></div>
                    )}

                    {userId != sheet?.created_by_user && (
                      <Badge bg="white" className="border text-dark fw-bold px-2 py-1 ms-2" style={{ fontSize: '10px' }}>
                        BY {sheet?.user_first_name?.toUpperCase()}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="d-flex align-items-center gap-3">
                  {tab !== "shared" && (
                    <Button
                      variant="link"
                      className="text-muted p-2 trash-btn"
                      onClick={(e) => { e.stopPropagation(); setSheetToDelete(sheet.id); setShowModal(true); }}
                    >
                      <RiDeleteBin6Line size={20} />
                    </Button>
                  )}
                  <RiArrowRightSLine size={24} className="text-muted opacity-25" />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {totalCount > 10 && (
        <PaginationComponent
          currentPage={currentPage}
          totalCount={totalCount}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      )}

      <Sheet ref={tableRef} onSave={() => {
        const page = new URLSearchParams(location.search).get('p') || 1;
        const crudFunction = tab === 'shared' ? sharedSheetsCrud : personalSheetsCrud;
        const setterFunction = tab === 'shared' ? setSharedSheets : setPersonalSheets;
        fetchSheets({ page: parseInt(page) }, crudFunction, setterFunction);
      }} />

      <DeleteModal
        show={showModal}
        onClose={() => setShowModal(false)}
        isLoading={loading}
        onConfirm={confirmDelete}
        title="Delete Document"
        message="Are you sure you want to permanently delete this document?"
      />

      <style>{`
        @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }
        .custom-loader {
            width: 30px;
            height: 30px;
            border-radius: 50%;
            border: 3px solid #f3f3f3;
            border-top: 3px solid #000;
            animation: spin 1s linear infinite;
        }
        .small-loader {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            border: 2px solid #f3f3f3;
            border-top: 2px solid ${BRAND_COLOR};
            animation: spin 0.8s linear infinite;
            flex-shrink: 0;
        }
        .sheet-row-item { 
          border-bottom: 1px solid #f1f5f9; 
          cursor: pointer; 
          transition: all 0.2s ease; 
        }
        .sheet-row-item:hover { 
          background-color: #f8f9fa; 
        }
        .trash-btn:hover { 
          color: #ef4444 !important; 
          background-color: #fef2f2; 
          border-radius: 8px; 
        }
        .nav-pills .nav-link.active { 
          background-color: ${BRAND_COLOR} !important; 
          color: black !important; 
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); 
        }
        .nav-pills .nav-link { 
          color: #64748b; 
        }
        .custom-checkbox:checked { 
          background-color: #000 !important; 
          border-color: #000 !important; 
        }
        .transition-all { 
          transition: all 0.3s ease; 
        }
      `}</style>
    </Container>
  );
};

export default DashboardTable;